import React from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceChecks = ({ complianceData }) => {
  const getCheckStatusIcon = (status) => {
    switch (status) {
      case 'pass': return { icon: 'CheckCircle', color: 'text-success' };
      case 'fail': return { icon: 'XCircle', color: 'text-error' };
      case 'warning': return { icon: 'AlertTriangle', color: 'text-warning' };
      case 'pending': return { icon: 'Clock', color: 'text-muted-foreground' };
      default: return { icon: 'HelpCircle', color: 'text-muted-foreground' };
    }
  };

  const getCheckStatusBg = (status) => {
    switch (status) {
      case 'pass': return 'bg-success/10';
      case 'fail': return 'bg-error/10';
      case 'warning': return 'bg-warning/10';
      case 'pending': return 'bg-muted/10';
      default: return 'bg-muted/10';
    }
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Compliance Checks</h2>
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={20} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {complianceData?.checks?.filter(check => check?.status === 'pass')?.length}/{complianceData?.checks?.length} Passed
          </span>
        </div>
      </div>
      <div className="space-y-6">
        {/* AML Screening */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="Search" size={20} className="mr-2" />
            AML Screening Results
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {complianceData?.amlScreening?.map((screening) => {
              const statusConfig = getCheckStatusIcon(screening?.status);
              return (
                <div
                  key={screening?.id}
                  className={`border rounded-lg p-4 ${getCheckStatusBg(screening?.status)}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Icon name={statusConfig?.icon} size={20} className={statusConfig?.color} />
                      <span className="font-medium text-foreground">{screening?.type}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusConfig?.color} bg-current/10`}>
                      {screening?.status}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Database:</span>
                      <span className="text-foreground">{screening?.database}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Matches:</span>
                      <span className="text-foreground">{screening?.matches}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Checked:</span>
                      <span className="text-foreground">{formatDate(screening?.checkedAt)}</span>
                    </div>
                    {screening?.notes && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-foreground text-xs">{screening?.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sanctions Check */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="AlertOctagon" size={20} className="mr-2" />
            Sanctions List Check
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {complianceData?.sanctionsCheck?.map((check) => {
              const statusConfig = getCheckStatusIcon(check?.status);
              return (
                <div
                  key={check?.id}
                  className={`border rounded-lg p-4 ${getCheckStatusBg(check?.status)}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Icon name={statusConfig?.icon} size={20} className={statusConfig?.color} />
                      <span className="font-medium text-foreground">{check?.listName}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusConfig?.color} bg-current/10`}>
                      {check?.status}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Authority:</span>
                      <span className="text-foreground">{check?.authority}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last Updated:</span>
                      <span className="text-foreground">{formatDate(check?.lastUpdated)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Confidence:</span>
                      <span className="text-foreground">{check?.confidence}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PEP Detection */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="Crown" size={20} className="mr-2" />
            PEP Detection
          </h3>
          <div className={`border rounded-lg p-4 ${getCheckStatusBg(complianceData?.pepCheck?.status)}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={getCheckStatusIcon(complianceData?.pepCheck?.status)?.icon} 
                  size={20} 
                  className={getCheckStatusIcon(complianceData?.pepCheck?.status)?.color} 
                />
                <span className="font-medium text-foreground">Politically Exposed Person Check</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${getCheckStatusIcon(complianceData?.pepCheck?.status)?.color} bg-current/10`}>
                {complianceData?.pepCheck?.status}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Risk Level:</span>
                <span className="text-foreground font-medium">{complianceData?.pepCheck?.riskLevel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Matches Found:</span>
                <span className="text-foreground">{complianceData?.pepCheck?.matchesFound}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Checked:</span>
                <span className="text-foreground">{formatDate(complianceData?.pepCheck?.lastChecked)}</span>
              </div>
            </div>

            {complianceData?.pepCheck?.details && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <h4 className="font-medium text-foreground mb-2">Additional Details</h4>
                <p className="text-sm text-foreground">{complianceData?.pepCheck?.details}</p>
              </div>
            )}
          </div>
        </div>

        {/* Source of Funds Validation */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="DollarSign" size={20} className="mr-2" />
            Source of Funds Validation
          </h3>
          <div className={`border rounded-lg p-4 ${getCheckStatusBg(complianceData?.sourceOfFunds?.status)}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={getCheckStatusIcon(complianceData?.sourceOfFunds?.status)?.icon} 
                  size={20} 
                  className={getCheckStatusIcon(complianceData?.sourceOfFunds?.status)?.color} 
                />
                <span className="font-medium text-foreground">Funds Origin Verification</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${getCheckStatusIcon(complianceData?.sourceOfFunds?.status)?.color} bg-current/10`}>
                {complianceData?.sourceOfFunds?.status}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Declared Source:</span>
                <span className="text-foreground">{complianceData?.sourceOfFunds?.declaredSource}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Verification Method:</span>
                <span className="text-foreground">{complianceData?.sourceOfFunds?.verificationMethod}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Documentation:</span>
                <span className="text-foreground">{complianceData?.sourceOfFunds?.documentation}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Risk Rating:</span>
                <span className="text-foreground">{complianceData?.sourceOfFunds?.riskRating}</span>
              </div>
            </div>

            {complianceData?.sourceOfFunds?.notes && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <h4 className="font-medium text-foreground mb-2">Validation Notes</h4>
                <p className="text-sm text-foreground">{complianceData?.sourceOfFunds?.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceChecks;